import {Component, HostListener, OnInit} from '@angular/core';
import {Observable} from 'rxjs';
import {createSelector, select, Store} from '@ngrx/store';
import {AppState} from '../store';
import {
  arePricingPlansReady, isAdmin,
  isLoggedIn,
  isLoggedOut,
  isSiteOwner,
  isTeamManager,
  selectTenantInfo,
  selectUser,
  userPictureUrl,
} from '../store/selectors';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {ActivatedRoute, Router, RouterLink, RouterLinkActive} from '@angular/router';
import {environment} from '../../environments/environment';
import {TenantService} from '../services/tenant.service';
import {checkIfPlatformSite, checkIfSingleSignOnPage, goToLoginPage,} from '../common/platform-utils';
import {User} from '../models/user.model';
import {MatDialog} from '@angular/material/dialog';
import {NewsletterDialogComponent} from '../newsletter-dialog/newsletter-dialog.component';
import {UiEventsService} from '../services/ui-events.service';
import {MenuEntry, TenantInfo} from '../models/tenant.model';
import {createDefaultDialogConfig} from '../common/dialog-utils';
import {LoadingService} from '../shared/services/loading.service';
import {ResponsiveLayoutService} from '../services/responsive-layout.service';
import {TranslateModule} from '@ngx-translate/core';
import {AllowRoleDirective} from '../shared/auth/allow-role.directive';
import {AllowPlanDirective} from '../shared/allow-plan/allow-plan.directive';
import {DenyRoleDirective} from '../shared/auth/deny-role.directive';
import {AsyncPipe, NgClass, NgFor, NgIf} from '@angular/common';
import {AiGlobalChatService} from '../ai-global-chat/ai-global-chat.service';
import {UrlBuilderService} from '../services/url-builder.service';
import {MessagesService} from '../shared/services/messages.service';
import { TrialExpirationComponent } from '../shared/trial-expiration/trial-expiration.component';

interface TopMenuData {
  tenant: TenantInfo;
  isLoggedIn: boolean;
  isLoggedOut: boolean;
  pictureUrl: string;
  arePricingPlansReady: boolean;
  user: User;
  showSubscription: boolean;
  isTeamManager: boolean;
  firstExtraMenuEntry: MenuEntry;
  moreMenuEntries: MenuEntry[];
}

@Component({
  selector: 'top-menu',
  templateUrl: './top-menu.component.html',
  styleUrls: ['./top-menu.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    RouterLink,
    RouterLinkActive,
    DenyRoleDirective,
    AllowPlanDirective,
    AllowRoleDirective,
    NgFor,
    AsyncPipe,
    TranslateModule,
    TrialExpirationComponent
  ],
})
export class TopMenuComponent implements OnInit {
  data$: Observable<TopMenuData>;

  isPlatformSite: boolean;
  isSingleSignOnPage: boolean;
  isLandingPageMode = false;

  isFullScreenMode = false;
  isPhonePortrait: boolean;

  environment: any;

  isTenantLoginPage = false;

  //mobile menu toggle
  isOpen = false;

  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    console.log('Back button pressed');
    this.aiChatService.endGlobalAiChatSession();
  }

  toggleMenu() {
    this.isOpen = !this.isOpen;
  }

  //onClickMenuItem
  isOpenDropdown = false;

  onClickMenuItem(event: Event) {
    event.stopPropagation();
    this.isOpenDropdown = !this.isOpenDropdown;
  }

  constructor(
    private store: Store<AppState>,
    private afAuth: AngularFireAuth,
    private router: Router,
    private loading: LoadingService,
    private messages: MessagesService,
    private tenant: TenantService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private uiEvents: UiEventsService,
    private aiChatService: AiGlobalChatService,
    private responsive: ResponsiveLayoutService,
    private ub: UrlBuilderService,) {

    this.isPlatformSite = checkIfPlatformSite();
    this.isSingleSignOnPage = checkIfSingleSignOnPage();
    this.environment = environment;
  }

  ngOnInit() {

    const selectTopMenuData = createSelector(
      selectTenantInfo,
      isLoggedIn,
      isLoggedOut,
      userPictureUrl,
      arePricingPlansReady,
      selectUser,
      isTeamManager,
      isAdmin,
      (
        tenant,
        isLoggedIn,
        isLoggedOut,
        pictureUrl,
        arePricingPlansReady,
        user,
        isTeamManager,
        isAdmin
      ) => {

        const allExtraMenuEntries = tenant?.customMenu ?? [];

        const firstExtraMenuEntry = allExtraMenuEntries[0],
          moreMenuEntries = allExtraMenuEntries.slice(1);

        return {
          tenant,
          isLoggedIn,
          isLoggedOut,
          pictureUrl,
          arePricingPlansReady,
          user,
          isTeamManager,
          showSubscription: isAdmin || (arePricingPlansReady && tenant?.isConnectedToStripe),
          firstExtraMenuEntry,
          moreMenuEntries
        };
      }
    );

    this.data$ = this.store.pipe(select(selectTopMenuData));

    this.isLandingPageMode =
      (window.location.search.includes('couponCode') ||
        window.location.search.includes('bundleCode')) &&
      !window.location.search.includes('purchaseResult');

    this.uiEvents.fullScreen$.subscribe(
      (fullScreenMode) => (this.isFullScreenMode = fullScreenMode)
    );

    this.responsive.isMobilePortait$.subscribe(
      (isPhonePortrait) => (this.isPhonePortrait = isPhonePortrait)
    );

    this.isTenantLoginPage = document.location.pathname.endsWith('login-page');

  }

  login(startInCoursesPage = false, tenantInfo: TenantInfo) {
    if (document.location.pathname?.endsWith('login-page')) {
      return;
    }
    goToLoginPage(this.router, startInCoursesPage, this.tenant.id);
  }

  logout(tenantInfo: TenantInfo) {
    this.afAuth.signOut().then(() => {
      this.loading.loadingOn();

      window.location.href = '/courses';
    });
  }

  showNewsletterPopup() {
    const dialogConfig = createDefaultDialogConfig();

    dialogConfig.width = '710px';

    this.dialog.open(NewsletterDialogComponent, dialogConfig);
  }

  onMenuClicked() {
    if (!window.location.hostname.startsWith('login')) {
    }
  }

  joinPartnerProgram() {
    window.open(
      'https://instructor-academy.onlinecoursehost.com/partner-program/',
      '_blank'
    );
  }

  openHelpCenter() {
    window.open('https://help.onlinecoursehost.com', '_blank');
  }

  closeAiGlobalChat() {
    this.aiChatService.endGlobalAiChatSession();
  }

  toggleAiChat(event, tenant: TenantInfo) {

    if (tenant?.instructorPricingPlan == 'growth') {
      this.messages.info(`The AI Course Creation Assistant is only available if you have a paid plan.`);
      return;
    }

    this.aiChatService.toggleAiChat()
    event.stopPropagation();
  }
}
